import { useState } from "react";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Trophy, ArrowDown, ArrowUp } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useRecords } from "@/hooks/useWeather";
import { formatDate, formatTime } from "@/lib/weatherUtils";
import { toast } from "sonner";

function iso(d) { return d.toISOString().slice(0, 10); }

const RECORD_ITEMS = [
  { key: "temperature_max", label: "Temperatura massima", field: "temperature", unit: "°C", icon: ArrowUp, color: "#EF4444" },
  { key: "temperature_min", label: "Temperatura minima", field: "temperature", unit: "°C", icon: ArrowDown, color: "#0EA5E9" },
  { key: "humidity_max", label: "Umidità massima", field: "humidity", unit: "%", icon: ArrowUp, color: "#0EA5E9" },
  { key: "humidity_min", label: "Umidità minima", field: "humidity", unit: "%", icon: ArrowDown, color: "#F97316" },
  { key: "pressure_max", label: "Pressione massima", field: "pressure", unit: "hPa", icon: ArrowUp, color: "#8B5CF6" },
  { key: "pressure_min", label: "Pressione minima", field: "pressure", unit: "hPa", icon: ArrowDown, color: "#8B5CF6" },
  { key: "wind_gust_max", label: "Raffica record", field: "wind_gust", unit: "km/h", icon: Trophy, color: "#FBBF24" },
  { key: "rain_day_max", label: "Pioggia in un giorno", field: "rain_day", unit: "mm", icon: Trophy, color: "#0EA5E9" },
];

export default function Storico() {
  const [date, setDate] = useState(new Date());
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { data: records } = useRecords();

  const search = async () => {
    setLoading(true);
    try {
      const r = await endpoints.searchDate(iso(date));
      setResult(r);
      if (!r.summary) toast.info("Nessun dato trovato per la data selezionata");
    } catch (e) {
      toast.error("Errore nella ricerca");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="page-storico" className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Archivio"
          title="Storico e record"
          subtitle="Consulta la memoria climatica della stazione: cerca per data, riepiloghi giornalieri e record assoluti."
        />

        {/* search */}
        <div className="metric-card p-6 md:p-8 mb-12" data-testid="storico-search">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-3">Ricerca per data</p>
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="date-picker-btn" className="rounded-full h-12 justify-start w-full md:w-72">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formatDate(date, { day: "2-digit", month: "long", year: "numeric" })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} initialFocus />
              </PopoverContent>
            </Popover>
            <Button data-testid="search-btn" onClick={search} disabled={loading} className="rounded-full h-12 px-8 bg-[#0B132B] hover:bg-[#0EA5E9]">
              {loading ? "Ricerca…" : "Cerca dati"}
            </Button>
          </div>

          {result?.summary && (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-4" data-testid="storico-summary">
              <Stat label="T. max" value={`${result.summary.t_max}°`} />
              <Stat label="T. min" value={`${result.summary.t_min}°`} />
              <Stat label="T. media" value={`${result.summary.t_avg}°`} />
              <Stat label="Umidità media" value={`${result.summary.h_avg}%`} />
              <Stat label="Pressione media" value={`${result.summary.p_avg} hPa`} />
              <Stat label="Pioggia" value={`${result.summary.rain} mm`} />
            </div>
          )}
        </div>

        {/* records */}
        <SectionHeader eyebrow="Record assoluti" title="I primati della stazione." />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="records-grid">
          {RECORD_ITEMS.map((r) => {
            const rec = records?.[r.key];
            return (
              <div key={r.key} className="metric-card p-6">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{r.label}</p>
                  <span className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: `${r.color}14`, color: r.color }}>
                    <r.icon className="h-4 w-4" />
                  </span>
                </div>
                <p className="mt-5 font-display text-4xl font-light text-[#0B132B]">
                  {rec ? `${rec[r.field]}` : "—"}
                  {rec && <span className="text-sm text-slate-500 ml-1.5">{r.unit}</span>}
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  {rec ? formatDate(rec.ts, { day: "2-digit", month: "long", year: "numeric" }) : "Nessun dato"}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
      <p className="font-display text-2xl text-[#0B132B] mt-1">{value}</p>
    </div>
  );
}
